import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import AddressSection from "./AddressSection";
import DeliveryModeSection from "./DeliveryModeSection";
import PaymentSection from "./PaymentSection";
import ItemsSection from "./ItemSection";
import OrderSummary from "./OrderSummarySection.jsx";
import CheckoutSteps from "./CheckoutSteps";
import { CheckOutContext } from './CheckOutContext.js';
import { fetchAddressesByUserID, fetchCounties, Order, ConfirmPayment, QueryPaymentStatus, GetCartItems } from "../Data.js";
import { v4 as uuidv4 } from "uuid";
import Dialogs from "./Dialogs.js";
import { useNavigate } from 'react-router-dom';
import { FaShieldAlt, FaLock, FaCreditCard, FaMobile, FaCheckCircle, FaSpinner, FaExclamationTriangle, FaArrowLeft } from 'react-icons/fa';
import { isUserLoggedIn, getGuestCart } from '../utils/guestUser';
import GuestCheckoutForm from './GuestCheckoutForm';



const MainCheckOutPage = () => {
  const navigate = useNavigate();

  // Initialize state from localStorage with error handling and validation
  const initializeState = () => {
    try {
      const savedState = localStorage.getItem('checkoutState');
      const parsed = savedState ? JSON.parse(savedState) : null;
      
      // Validate parsed state structure
      if (parsed && typeof parsed === 'object') {
        return {
          userID: localStorage.getItem("userID") || "",
          isLoading: false,
          addresses: Array.isArray(parsed.addresses) ? parsed.addresses : [],
          counties: Array.isArray(parsed.counties) ? parsed.counties : [],
          selectedAddress: parsed.selectedAddress || null,
          isAddressSelected: Boolean(parsed.isAddressSelected),
          deliveryModes: Array.isArray(parsed.deliveryModes) ? parsed.deliveryModes : [],
          paymentDetails: Array.isArray(parsed.paymentDetails) ? parsed.paymentDetails : [],
          selectedDeliveryMode: parsed.selectedDeliveryMode || null,
          showDeliveryModeForm: Boolean(parsed.showDeliveryModeForm),
          deliveryScheduleDate: parsed.deliveryScheduleDate || null,
          pickupStation: parsed.pickupStation || "",
          isDeliveryModeSelected: Boolean(parsed.isDeliveryModeSelected),
          isDeliveryModeConfirmed: Boolean(parsed.isDeliveryModeConfirmed),
          paymentMethods: Array.isArray(parsed.paymentMethods) ? parsed.paymentMethods : [],
          selectedPaymentMethod: parsed.selectedPaymentMethod || null,
          showPaymentForm: Boolean(parsed.showPaymentForm),
          isPaymentMethodSelected: Boolean(parsed.isPaymentMethodSelected),
          shippingCost: typeof parsed.shippingCost === 'number' ? parsed.shippingCost : 81.03,
          totalOrderedAmount: typeof parsed.totalOrderedAmount === 'number' ? parsed.totalOrderedAmount : 0,
          totalDeliveryFees: typeof parsed.totalDeliveryFees === 'number' ? parsed.totalDeliveryFees : 0,
          totalPaymentAmount: typeof parsed.totalPaymentAmount === 'number' ? parsed.totalPaymentAmount : 0,
          showAddressForm: Boolean(parsed.showAddressForm),
          showSuccessDialog: false, // Always reset dialogs on page load
          showErrorDialog: false,
          successMessage: "",
          errorMessage: "",
          currentStep: parsed.currentStep || 1,
          isProcessingOrder: false,
          orderValidationErrors: [],
          retryCount: 0
        };
      }
    } catch (error) {
      console.error('Error parsing saved checkout state:', error);
      localStorage.removeItem('checkoutState'); // Clear corrupted state
    }
    
    // Return default state if no valid saved state
    return {
      userID: localStorage.getItem("userID") || "",
      isLoading: false,
      addresses: [],
      counties: [],
      selectedAddress: null,
      isAddressSelected: false,
      deliveryModes: [],
      paymentDetails: [],
      selectedDeliveryMode: null,
      showDeliveryModeForm: false,
      deliveryScheduleDate: null,
      pickupStation: "",
      isDeliveryModeSelected: false,
      isDeliveryModeConfirmed: false,
      paymentMethods: [],
      selectedPaymentMethod: null,
      showPaymentForm: false,
      isPaymentMethodSelected: false,
      shippingCost: 81.03,
      totalOrderedAmount: 0,
      totalDeliveryFees: 0,
      totalPaymentAmount: 0,
      showAddressForm: false,
      showSuccessDialog: false,
      showErrorDialog: false,
      successMessage: "",
      errorMessage: "",
      currentStep: 1,
      isProcessingOrder: false,
      orderValidationErrors: [],
      retryCount: 0
    };
  };
 // Order Data State
  const [orderData, setOrderData] = useState({
    OrderID: "",
    paymentDetails: [],
    products: [],
    status: "",
    PaymentConfirmation: "",
    totalPaymentAmount: 0,
    totalDeliveryFees: 0,
    totalTax: 0,
    shippingAddress: "",
    deliveryMode: "",
    pickupLocation: ""
  });

  const [state, setState] = useState(initializeState);
  const { checkOutData, subTotal, clearCheckout, addItemTocheckOut } = useContext(CheckOutContext);

  // Memoized calculations for performance
  const calculations = useMemo(() => {
    const itemsTotal = checkOutData.reduce((total, item) => total + (item.price * item.quantity), 0);
    const deliveryFees = state.selectedDeliveryMode === 2 ? state.shippingCost : 0; // Home delivery has fees
    const taxAmount = 0; // Add tax calculation if needed
    const finalTotal = itemsTotal + deliveryFees + taxAmount;
    
    return {
      itemsTotal,
      deliveryFees,
      taxAmount,
      finalTotal
    };
  }, [checkOutData, state.shippingCost, state.selectedDeliveryMode]);

  // Determine current step based on completed actions
  const currentStep = useMemo(() => {
    if (state.isPaymentMethodSelected && calculations.finalTotal > 0) return 4; // Review step
    if (state.isDeliveryModeConfirmed) return 3; // Payment step
    if (state.isAddressSelected) return 2; // Delivery step
    return 1; // Address step
  }, [state.isAddressSelected, state.isDeliveryModeConfirmed, state.isPaymentMethodSelected, calculations.finalTotal]);

  // Save state to localStorage with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('checkoutState', JSON.stringify({
          ...state,
          currentStep // Always save current step
        }));
      } catch (error) {
        console.error('Failed to save checkout state:', error);
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [state, currentStep]);

  // Helper function to update state with validation
  const updateState = useCallback((newState) => {
    setState(prev => {
      const updated = { ...prev, ...newState };
      // Auto-update totals when relevant fields change
      if (newState.selectedDeliveryMode !== undefined || checkOutData.length !== prev.checkOutDataLength) {
        updated.totalOrderedAmount = calculations.itemsTotal;
        updated.totalDeliveryFees = calculations.deliveryFees;
        updated.totalPaymentAmount = calculations.finalTotal;
        updated.checkOutDataLength = checkOutData.length;
      }
      return updated;
    });
  }, [calculations]);

  // Sync server cart with local checkout context on component mount
  useEffect(() => {
    const syncCartData = async () => {
      const userID = localStorage.getItem("userID");
      if (userID && checkOutData.length === 0) {
        try {
          const serverCartItems = await GetCartItems(userID);
          if (serverCartItems && serverCartItems.length > 0) {
            // Convert server cart items to checkout context format
            serverCartItems.forEach(item => {
              const checkoutItem = {
                productID: item.productId,
                productName: item.productName,
                price: item.price,
                quantity: item.quantity,
                productImage: item.productImage,
                inStock: item.inStock !== false,
                merchantId : item.merchantId
              };
              addItemTocheckOut(checkoutItem);
            });
          }
        } catch (error) {
          console.error("Error syncing cart data:", error);
        }
      }
    };
    
    syncCartData();
  }, [addItemTocheckOut]); // Only run once when component mounts

  // Enhanced address loading with retry logic
  const loadAddresses = useCallback(async (retryAttempt = 0) => {
    const maxRetries = 3;
    
    if (!localStorage.getItem("userID")) {
      return; // Skip if no user ID
    }
    
    updateState({ isLoading: true, errorMessage: "" });
    
    try {
      const fetchedAddresses = await fetchAddressesByUserID(localStorage.getItem("userID"));
      updateState({ 
        addresses: Array.isArray(fetchedAddresses) ? fetchedAddresses : [],
        isLoading: false,
        retryCount: 0
      });
    } catch (error) {
      if (retryAttempt < maxRetries) {
        setTimeout(() => {
          loadAddresses(retryAttempt + 1);
        }, 1000 * (retryAttempt + 1)); // Progressive delay
        
        updateState({ 
          retryCount: retryAttempt + 1,
          errorMessage: `Loading addresses... (attempt ${retryAttempt + 2}/${maxRetries + 1})`
        });
      } else {
        handleError(error, 'address');
      }
    }
  }, [updateState]);

  // Error handler function
  const handleError = useCallback((error, context = 'general') => {
    console.error(`Error in ${context}:`, error);
    updateState({ 
      errorMessage: `Failed to load ${context}. Please try again.`,
      isLoading: false 
    });
  }, [updateState]);

  // Fetch Addresses with improved error handling
  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  // Fetch Counties
  useEffect(() => {
    const loadCounties = async () => {
      updateState({ isLoading: true });
      try {
        const fetchedCounties = await fetchCounties();
        updateState({ counties: fetchedCounties, isLoading: false });
      } catch (error) {
        console.error("Error fetching counties:", error);
        updateState({ 
          errorMessage: "Failed to load counties. Please try again.",
          showErrorDialog: true,
          isLoading: false 
        });
      }
    };
    loadCounties();
  }, []);

  // Fetch Delivery Modes
  useEffect(() => {
    const modes = [
      { id: 1, name: "Pick Up Station", logo: "/Images/location.png" },
      { id: 2, name: "Home Delivery", logo: "/Images/homedelivery.png" },
    ];
    updateState({ deliveryModes: modes });
  }, []);

  // Update Delivery Modes Based on Selected Address
  useEffect(() => {
    if (state.selectedAddress) {
      const modes = [
        { id: 1, name: "Pick Up Station", logo: "/Images/location.png" },
        { id: 2, name: "Home Delivery", logo: "/Images/homedelivery.png" },
      ];
      updateState({ deliveryModes: modes });
    } else {
      updateState({ deliveryModes: [] });
    }
  }, [state.selectedAddress]);

  // Update Payment Methods Based on Selected Delivery Mode
  useEffect(() => {
    if (state.selectedDeliveryMode) {
      const methods = [
        { id: 1, name: "Mpesa", logo: "/Images/Mpesa.jpeg", paymentMethod: "Mpesa" }
        // { id: 2, name: "Credit Card", logo: "/Images/card-logo-compact.gif", paymentMethod: "CreditCard" },
        // { id: 3, name: "Pay on Delivery", logo: "/Images/delivery-track-icon.png", paymentMethod: "Cash" },
      ];
      updateState({ paymentMethods: methods });
    } else {
      updateState({ paymentMethods: [] });
    }
  }, [state.selectedDeliveryMode]);

  // Event Handlers
  const handleSelectDeliveryMode = (modeId) => {
    updateState({ 
      selectedDeliveryMode: modeId,
      isDeliveryModeSelected: true,
      errorMessage: state.isAddressSelected ? null : state.errorMessage,
      showErrorDialog: state.isAddressSelected ? false : state.showErrorDialog
    });

    if (modeId === 1) {
      updateState({ showDeliveryModeForm: true });
    } else if (modeId === 2) {
      handleConfirmDeliveryMode();
    }
  };

  const clearDeliveryMode = useCallback(() => {
    updateState({ 
      isDeliveryModeSelected: false,
      selectedDeliveryMode: null,
      showDeliveryModeForm: false 
    });
  }, []);

  const handleConfirmDeliveryMode = useCallback(() => {
    updateState({ isDeliveryModeConfirmed: true });
  }, []);

  const handleSelectPaymentMethod = (methodId) => {
    updateState({ 
      selectedPaymentMethod: methodId,
      showPaymentForm: true,
      isPaymentMethodSelected: true,
      errorMessage: null,
      showErrorDialog: false
    });
  };

  const handleCloseModal = () => {
    updateState({ 
      showAddressForm: false,
      showSuccessDialog: false,
      showErrorDialog: false 
    });
    window.history.pushState(null, "", "/MainCheckout");
  };

  const handleUpdateAddresses = useCallback(async () => {
    try {
      const updatedAddresses = await fetchAddressesByUserID(state.userID);
      updateState({ addresses: updatedAddresses });
    } catch (error) {
      console.error("Error updating addresses:", error);
      updateState({ 
        errorMessage: "Failed to update addresses. Please try again.",
        showErrorDialog: true 
      });
    }
  }, [state.userID]);

  const handleAddressSelection = useCallback((address) => {
    updateState({ 
      selectedAddress: address,
      isAddressSelected: true 
    });
  }, []);

  const handleAddNewAddress = () => {
    updateState({ 
      showAddressForm: true,
      selectedAddress: null 
    });
  };

  const HandleCloseDialog = () => {
    updateState({ showErrorDialog: false });
  };

  const handleEditAddress = useCallback((address) => {
    updateState({ 
      selectedAddress: address,
      showAddressForm: true,
      isAddressSelected: true 
    });
  }, []);

  const clearPaymentMethod = useCallback(() => {
    updateState({ 
      isPaymentMethodSelected: false,
      selectedPaymentMethod: null 
    });
  }, []);

  // Generate Order ID
  const GenerateOrderID = useCallback(() => {
    const timestamp = new Date().getTime();
    const uuid = uuidv4();
    return `ORD-${timestamp}-${uuid.substring(0, 4)}`;
  }, []);

  // Set Products in Order Data
  const SetProducts = useCallback((checkOutData) => {
    const productsArray = checkOutData.map((product) => ({
      productID: product.productID,
      productName: product.productName,
      quantity: product.quantity,
      price: product.price,
      discount: 0,
      deliveryFee: 0,
      merchantId: product.merchantId
    }));
    
    updateState(prev => ({
      orderData: {
        paymentDetails: [], // Reset or maintain paymentDetails
        ...prev.orderData,
        products: productsArray,
      }
    }));
  }, []);

  // Update Order Data When CheckOutData Changes
  useEffect(() => {
    if (checkOutData) {
      const newOrderID = GenerateOrderID();
      updateState(prev => ({
        orderData: {
          ...prev.orderData,
          OrderID: newOrderID,
        }
      }));
      SetProducts(checkOutData);
    }
  }, [checkOutData, GenerateOrderID, SetProducts]);

  const calculateOrderTotal = () => {
    let totalAmount = 0;
    let deliveryFees = 0;
  
    const Zones = {
      "CBD": 100,
      "Zone2": 200,
      "Zone3": 300,
      "Zone4": 400,
    };
  
    let selectedZone = "CBD";
  
    checkOutData.forEach((product) => {
      totalAmount += product.price * product.quantity;
    });
  
    if (totalAmount > 2500 && selectedZone === "CBD") {
      deliveryFees = 0;
    } else {
      deliveryFees = Zones[selectedZone] || 400;
    }
  
    // const totalPaymentAmount = totalAmount + deliveryFees;

    const totalPaymentAmount = totalAmount ;
  
    updateState({
      totalDeliveryFees: deliveryFees,
      totalOrderedAmount: totalAmount,
      totalPaymentAmount: totalPaymentAmount
    });
  };

  useEffect(() => {
    calculateOrderTotal();
  }, [subTotal]);

  const createPayload = () => {
    const orderID = GenerateOrderID();
  
    const products = checkOutData.map((product) => ({
      productID: product.productID,
      merchantId: product.merchantId,
      productName: product.productName,
      quantity: product.quantity,
      price: product.price,
      deliveryFee: 0,
      discount: 0,
      imageUrl: product.imageUrl || ""
    }));
  
   // In createPayload: 
    const paymentDetails = [{
      paymentID: uuidv4(), // Generate UUID for paymentID
      paymentMethodID: state.selectedPaymentMethod,
      paymentReference: parseInt(orderData.paymentDetails?.[0]?.phoneNumber) || 0, // Safe access
      trxReference: "", // Will be updated with checkout request ID for M-Pesa
      paymentMethod: state.paymentMethods.find((method) => method.id === state.selectedPaymentMethod)?.name || "Unknown",
      phonenumber: orderData.paymentDetails?.[0]?.phoneNumber || state.selectedAddress?.phoneNumber || '', // Use address phone as fallback
      amount: state.totalPaymentAmount,
      paymentDate: new Date().toISOString()
    }];

   
  
    const shippingAddress = state.selectedAddress
      ? {
          name: state.selectedAddress.name,
          phonenumber: state.selectedAddress.phoneNumber,
          address: state.selectedAddress.extraInformation || "",
          county: state.selectedAddress.county,
          town: state.selectedAddress.town,
          postalCode: state.selectedAddress.postalCode
        }
      : {};
  
    const deliveryMode = state.deliveryModes.find((mode) => mode.id === state.selectedDeliveryMode)?.name || "Unknown";
    const pickUpLocation = state.selectedDeliveryMode === 1
      ? {
          countyId: 0,
          townId: 0,
          deliveryStationId: 0,
        }
      : {};
  
    return {
      orderID: orderID,
      applicationUserId: localStorage.getItem("userID"),
      orderDate: new Date().toISOString(),
      deliveryScheduleDate: state.deliveryScheduleDate ? new Date(state.deliveryScheduleDate).toISOString() : new Date().toISOString(),
      orderedBy: localStorage.getItem("username"),
      status: "1", // String format as expected
      paymentDetails: paymentDetails,
      products: products,
      paymentConfirmation: "Pending",
      totalOrderAmount: state.totalOrderedAmount,
      totalPaymentAmount: state.totalPaymentAmount,
      totalDeliveryFees: state.totalDeliveryFees,
      totalTax: 0,
      shippingAddress: shippingAddress,
      pickUpLocation: pickUpLocation
    };
  };
  
  // Helper function to poll M-Pesa payment status
  const pollPaymentStatus = async (checkoutRequestID, maxAttempts = 30, intervalMs = 2000) => {
    let attempts = 0;
    
    return new Promise((resolve, reject) => {
      const poll = async () => {
        attempts++;
        
        try {
          // Call your backend API to check payment status
          const statusData = await QueryPaymentStatus(checkoutRequestID);
          
          if (statusData.resultCode === "00") {
            // Payment successful
            resolve({
              success: true,
              transactionID: statusData.mpesaReceiptNumber,
              message: "Payment completed successfully"
            });
          } else if (statusData.resultCode && statusData.resultCode !== "0" && statusData.resultCode !== "1032") {
            // Payment failed (not pending)
            resolve({
              success: false,
              message: statusData.resultDesc || "Payment failed"
            });
          } else if (attempts >= maxAttempts) {
            // Timeout reached
            resolve({
              success: false,
              message: "Payment timeout. Please check your M-Pesa messages and contact support if payment was deducted."
            });
          } else {
            // Still pending, continue polling
            setTimeout(poll, intervalMs);
          }
        } catch (error) {
          if (attempts >= maxAttempts) {
            resolve({
              success: false,
              message: "Unable to verify payment status. Please contact support."
            });
          } else {
            setTimeout(poll, intervalMs);
          }
        }
      };
      
      poll();
    });
  };

  const PlaceOrder = async () => {
  // Validate environment variables
  if (!process.env.REACT_APP_MPESA_BUSINESS_SHORT_CODE || 
      !process.env.REACT_APP_MPESA_PARTY_B || 
      !process.env.REACT_APP_MPESA_CALLBACK_URL ||
      !process.env.REACT_APP_API_BASE_URL) {
    updateState({
      errorMessage: "M-Pesa configuration is missing. Please contact support.",
      showErrorDialog: true
    });
    return;
  }

  if (!state.isAddressSelected) {
    updateState({
      errorMessage: "Please Select Address to continue!",
      showErrorDialog: true
    });
    return;
  }
  if (!state.isDeliveryModeSelected) {
    updateState({
      errorMessage: "Please Select Delivery Mode to continue!",
      showErrorDialog: true
    });
    return;
  }
  if (!state.isPaymentMethodSelected) {
    updateState({
      errorMessage: "Please choose payment Mode to continue!",
      showErrorDialog: true
    });
    return;
  }

  const payload = createPayload();
  
  // Validate phone number before proceeding
  const phoneNumber = payload.paymentDetails[0].phonenumber;
  if (!phoneNumber || phoneNumber.trim() === '') {
    updateState({
      errorMessage: "Phone number is required for M-Pesa payment. Please ensure your address has a valid phone number.",
      showErrorDialog: true
    });
    return;
  }

  // Validate phone number format (should be 12 digits starting with 254)
  const phoneRegex = /^254\d{9}$/;
  if (!phoneRegex.test(phoneNumber)) {
    updateState({
      errorMessage: "Invalid phone number format. Please use format: 254XXXXXXXXX",
      showErrorDialog: true
    });
    return;
  }

  let checkoutRequestID = null;

  try {
    // Step 1: Initiate STK Push
    updateState({
      successMessage: "Initiating M-Pesa payment...",
      showSuccessDialog: true
    });

    const stkPushData = {
      businessShortCode: process.env.REACT_APP_MPESA_BUSINESS_SHORT_CODE,
      phoneNumber: payload.paymentDetails[0].phonenumber,
      amount: payload.totalPaymentAmount.toString(),
      partyA: payload.paymentDetails[0].phonenumber,
      partyB: process.env.REACT_APP_MPESA_PARTY_B,
      callbackURL: process.env.REACT_APP_MPESA_CALLBACK_URL,
      accountReference: payload.orderID,
      transactionDesc: `Payment for order ${payload.orderID}`
    };

    const stkPushResponse = await ConfirmPayment(stkPushData);

    if (stkPushResponse && stkPushResponse.responseCode === 0) {
      checkoutRequestID = stkPushResponse.checkoutRequestID;
      
      // Update payment details with checkout request ID
      payload.paymentDetails[0] = {
        ...payload.paymentDetails[0],
        trxReference: checkoutRequestID
      };

      // Step 2: Show user prompt and start polling
      updateState({
        successMessage: "Please check your phone and enter your M-Pesa PIN to complete the payment. Do not close this page...",
        showSuccessDialog: true
      });

      // Step 3: Poll for payment confirmation
      const paymentResult = await pollPaymentStatus(checkoutRequestID);

      if (paymentResult.success) {
        // Step 4: Payment confirmed, create order
        updateState({
          successMessage: "Payment confirmed! Creating your order...",
          showSuccessDialog: true
        });

        // Add M-Pesa transaction ID to payment details
        // payload.paymentDetails[0] = {
        //   ...payload.paymentDetails[0],
        //   mpesaTransactionID: paymentResult.transactionID
        // };

        const orderData = { orders: [payload] };
        const response = await Order(orderData);

        // Updated response handling for new API endpoint format
        if (response && response.success === true && response.orderCreated === true) {
          updateState({
            successMessage: "Order created successfully! Redirecting to your orders...",
            showSuccessDialog: true
          });

          // Check if this was a Buy Now order to restore previous cart state
          const hasBuyNowItems = checkOutData.some(item => item.buyNow === true);
          let previousCartData = null;
          
          if (hasBuyNowItems) {
            // Restore previous cart state for Buy Now orders
            try {
              const savedPreviousCart = localStorage.getItem('previousCheckoutData');
              if (savedPreviousCart) {
                previousCartData = JSON.parse(savedPreviousCart);
                localStorage.removeItem('previousCheckoutData'); // Clean up
              }
            } catch (error) {
              console.error('Error restoring previous cart state:', error);
            }
          }

          // Clear all checkout data after successful order
          localStorage.removeItem("checkoutState");
          localStorage.removeItem("checkOutData");
          localStorage.removeItem("checkOutSubTotal");

          // Clear the cart using context
          clearCheckout();

          // Restore previous caart state if it was a Buy Now order
          if (previousCartData && previousCartData.length > 0) {
            setTimeout(() => {
              previousCartData.forEach(item => {
                addItemTocheckOut(item);
              });
            }, 100); // Small delay to ensure cleanup is complete
          }

          // Navigate to orders page after 3 seconds
          setTimeout(() => {
            navigate("/ReturnsAndOrdersPage");
          }, 6000);
        } else {
          updateState({
            errorMessage: response?.message || "Failed to create order after payment. Please contact support with your M-Pesa transaction ID.",
            showErrorDialog: true,
            showSuccessDialog: false
          });
        }
      } else {
        // Payment failed or timed out
        updateState({
          errorMessage: paymentResult.message,
          showErrorDialog: true,
          showSuccessDialog: false
        });
      }
    } else {
      // STK Push failed
      updateState({
        errorMessage: stkPushResponse?.responseMessage || "Failed to initiate M-Pesa payment. Please try again.",
        showErrorDialog: true,
        showSuccessDialog: false
      });
    }
  } catch (error) {
    console.error("Payment error:", error);
    updateState({
      errorMessage: "An error occurred during payment processing. Please try again.",
      showErrorDialog: true,
      showSuccessDialog: false
    });
  }
};


  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Checkout</h1>
        
        {/* Checkout Steps Progress */}
        <CheckoutSteps 
          currentStep={
            !state.isAddressSelected ? 1 :
            !state.isDeliveryModeConfirmed ? 2 :
            !state.isPaymentMethodSelected ? 3 : 4
          }
        />
        
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Left Section - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Address Section */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              {state.isLoading ? (
                <div className="p-6 flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <AddressSection
                  addresses={state.addresses}
                  selectedAddress={state.selectedAddress}
                  handleAddressSelection={handleAddressSelection}
                  handleEditAddress={handleEditAddress}
                  showAddressForm={state.showAddressForm}
                  setShowAddressForm={(value) => updateState({ showAddressForm: value })}
                  handleCloseModal={handleCloseModal}
                  onUpdateAddresses={handleUpdateAddresses}
                  counties={state.counties}
                  userID={state.userID}
                  handleAddNewAddress={handleAddNewAddress}
                />
              )}
            </div>

            {/* Delivery Mode Section */}
            {state.isAddressSelected && (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <DeliveryModeSection
                  deliveryModes={state.deliveryModes}
                  selectedDeliveryMode={state.selectedDeliveryMode}
                  handleSelectDeliveryMode={handleSelectDeliveryMode}
                  setShowDeliveryModeForm={(value) => updateState({ showDeliveryModeForm: value })}
                  showDeliveryModeForm={state.showDeliveryModeForm}
                  isAddressSelected={state.isAddressSelected}
                  deliveryScheduleDate={state.deliveryScheduleDate}
                  setDeliveryScheduleDate={(date) => updateState({ deliveryScheduleDate: date })}
                  pickupStation={state.pickupStation}
                  setPickupStation={(station) => updateState({ pickupStation: station })}
                  selectedAddress={state.selectedAddress}
                  isDeliveryModeSelected={state.isDeliveryModeSelected}
                  setIsDeliveryModeSelected={(value) => updateState({ isDeliveryModeSelected: value })}
                  clearDeliveryMode={clearDeliveryMode}
                  handleConfirmDeliveryMode={handleConfirmDeliveryMode}
                  counties={state.counties}
                />
              </div>
            )}

            {/* Payment Section */}
            {state.isDeliveryModeConfirmed && (
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <PaymentSection
              paymentMethods={state.paymentMethods}
              selectedPaymentMethod={state.selectedPaymentMethod}
              handleSelectPaymentMethod={handleSelectPaymentMethod}
              showPaymentForm={state.showPaymentForm}
              setShowPaymentForm={(value) => updateState({ showPaymentForm: value })}
              setOrderData={setOrderData}
              orderData={orderData}
              subTotal={subTotal}
              shippingCost={state.shippingCost}
              isPaymentMethodSelected={state.isPaymentMethodSelected}
              clearPaymentMethod={clearPaymentMethod}
              setSelectedPaymentMethod={(method) => updateState({ selectedPaymentMethod: method })}
            />
              </div>
            )}

            {/* Items Section */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <ItemsSection 
                checkOutData={checkOutData}
                subTotal={subTotal}
                isPaymentMethodSelected={state.isPaymentMethodSelected}
              />
            </div>
          </div>

          {/* Right Section - Order Summary */}
          <div className="mt-8 lg:mt-0">
            <div className="bg-white shadow rounded-lg overflow-hidden sticky top-8">
              <OrderSummary
                checkOutData={checkOutData}
                totalOrderedAmount={state.totalOrderedAmount}
                totalDeliveryFees={state.totalDeliveryFees}
                totalPaymentAmount={state.totalPaymentAmount}
                PlaceOrder={PlaceOrder}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Success and Error Dialogs */}
      {state.showSuccessDialog && (
        <Dialogs 
          message={state.successMessage}
          type="success"
          onClose={() => updateState({ showSuccessDialog: false })}
        />
      )}
      {state.showErrorDialog && (
        <Dialogs 
          message={state.errorMessage}
          type="error"
          onClose={HandleCloseDialog}
        />
      )}
    </div>
  );
};

export default MainCheckOutPage;
